import React, { useEffect, useMemo, useState } from "react";
import styles from "./syllabus.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaPlus } from "react-icons/fa";
import PopupModal from "../../../../components/PopupModal/PopupModal";

function SyllabusCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { CLASSID } = location.state || {};

  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const client_id = user?.client_data[0].client_id || "";
  const clientData = user?.client_data[0] || [];
  const school_id = clientData?.school_id || "";
  // const academic_year = clientData?.academic_year || "";
  const academic_id = clientData?.academic_year_id || "";

  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    chapter: "",
    units: "",
    short_description: "",
    description: "",
    page_from: "",
    page_to: "",
    examType: "",
  });

  const [data, setData] = useState([]);
  const [examData, setExamData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // ✅ Modal Popup state
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });

  const handleAdd = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    setSubmitted(true);

    if (Object.keys(validationErrors).length > 0) return;

    const newRow = {
      chapter: formData.chapter,
      units: formData.units,
      short_description: formData.short_description,
      description: formData.description,
      page_from: formData.page_from,
      page_to: formData.page_to,
      examType: formData.examType?.label || "",
      exam_plan: formData.examType?.value || "",
      class_subject_id: formData.subject.value,
      subject: formData.subject,
      isActive: formData.isActive || 0,
    };

    setTableData((prev) => [...prev, newRow]);

    setFormData({
      subject: formData.subject,
      chapter: "",
      units: "",
      short_description: "",
      description: "",
      page_from: "",
      page_to: "",
      examType: "",
    });
    setSubmitted(false);
    setErrors({});
  };

  useEffect(() => {
    fetchSubjectList();
    fetchExamList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubjectList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/school/listSubject", {
        page: "",
        limit: "",
        search: "",
      });

      if (response?.data?.rows) {
        const subjectData = response.data.rows;
        const data = subjectData.map((item) => ({
          label: item.subject_name,
          value: item.id,
        }));
        setData(data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching subject list", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExamList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/exam/examConfig/list", {
        school_id: school_id,
        academic_year: academic_id,
      });

      if (response?.data) {
        const exmaData = response.data.data;
        const data = exmaData.map((item) => ({
          label: item.exam_type,
          value: item.id,
        }));
        setExamData(data);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exam list", error);
      setExamData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const cleaned = value.replace(/\D/g, "").slice(0, 4);
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDropdownChange = (name, option) => {
    setFormData((prev) => ({ ...prev, [name]: option }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ✅ Helper to check if value is empty
  const isEmpty = (val) => {
    if (val === null || val === undefined) return true;
    if (typeof val === "object") return !val.value;
    return String(val).trim() === "";
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.subject)
      newErrors.subject = translations.errors.subjectRequired;
    if (!formData.chapter)
      newErrors.chapter = translations.errors.chapterRequired;
    if (!formData.units) newErrors.units = translations.errors.unitRequired;
    if (!formData.short_description)
      newErrors.short_description =
        translations.errors.shortDescriptionRequired;
    if (!formData.description)
      newErrors.description = translations.errors.descriptionRequired;
    if (!formData.page_from)
      newErrors.page_from = translations.errors.pageFromRequired;
    if (!formData.page_to)
      newErrors.page_to = translations.errors.pageToRequired;
    if (!formData.examType)
      newErrors.examType = translations.errors.examTypeRequired;
    return newErrors;
  };

  // ✅ Validate every row in tableData
  const validateTableRows = (rows) => {
    const requiredFields = [
      { key: "chapter", label: "Chapter" },
      { key: "units", label: "Units" },
      { key: "short_description", label: "Short Description" },
      { key: "description", label: "Description" },
      { key: "page_from", label: "Page From" },
      { key: "page_to", label: "Page To" },
      { key: "exam_plan", label: "Exam Type" },
      { key: "class_subject_id", label: "Subject" },
    ];

    for (let i = 0; i < rows.length; i++) {
      const missing = requiredFields
        .filter((f) => isEmpty(rows[i][f.key]))
        .map((f) => f.label);
      if (missing.length > 0) {
        return `Row ${i + 1}: Please fill ${missing.join(", ")}`;
      }
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalTableData = [...tableData];

    // ✅ If no rows added, validate the form fields
    if (tableData.length === 0) {
      const validationErrors = validateForm();
      setErrors(validationErrors);
      setSubmitted(true);

      if (Object.keys(validationErrors).length > 0) {
        const missingLabels = Object.keys(validationErrors).join(", ");
        setModalState({
          open: true,
          type: "error",
          message: `Please fill all required fields: ${missingLabels}`,
          redirectPath: "",
        });
        return;
      }

      const newRow = {
        chapter: formData.chapter,
        units: formData.units,
        short_description: formData.short_description,
        description: formData.description,
        page_from: formData.page_from,
        page_to: formData.page_to,
        exam_plan: formData.examType?.value || "",
        class_subject_id: formData.subject.value,
        isActive: formData.isActive || 0,
      };

      finalTableData = [newRow];
    }

    // ✅ Validate every row in the table before API call
    const tableError = validateTableRows(finalTableData);
    if (tableError) {
      setModalState({
        open: true,
        type: "error",
        message: tableError,
        redirectPath: "",
      });
      return;
    }

    const payloadSyllabus = finalTableData.map((row) => ({
      chapter: row.chapter,
      units: row.units,
      short_description: row.short_description,
      description: row.description,
      page_from: row.page_from,
      page_to: row.page_to,
      exam_plan: row.exam_plan,
      class_subject_id: row.class_subject_id,
      isActive: row.isActive || 0,
    }));

    const payload = {
      client_id: client_id,
      academic_id: academic_id,
      class_id: CLASSID,
      school_id: school_id,
      syllabus: payloadSyllabus,
      created_by: userId,
    };

    try {
      const response = await AxiosInstance.post(
        `/school/createSyllabus`,
        payload,
      );

      if (response.data.status) {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "Syllabus created successfully",
          redirectPath: {
            path: "/user/syllabusList",
            state: { ID: CLASSID },
          },
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Failed to create syllabus",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error(error);
      setModalState({
        open: true,
        type: "error",
        message: "Something went wrong. Please try again.",
        redirectPath: "",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
    setFormData({});
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath.path, { state: redirectPath.state });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "chapter",
        header: translations.labels.chapter,
      },
      {
        accessorKey: "units",
        header: translations.labels.unit,
      },
      {
        accessorKey: "short_description",
        header: translations.labels.shortDescription,
      },
      {
        accessorKey: "description",
        header: translations.labels.description,
      },
      {
        accessorKey: "page_from",
        header: translations.labels.pageFrom,
      },
      {
        accessorKey: "page_to",
        header: translations.labels.pageTo,
      },
      {
        accessorKey: "examType",
        header: translations.labels.examType,
      },
      {
        id: "action",
        header: "Remove",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <IconButton
            color="error"
            onClick={() => {
              setTableData((prev) =>
                prev.filter((_, index) => index !== row.index),
              );
            }}
          >
            <CloseIcon />
          </IconButton>
        ),
      },
    ],
    [translations],
  );

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        <form onSubmit={handleSubmit} className="row g-4">
          {/* Subject */}
          <div className="col-md-12 px-2">
            <CustomDropdown
              label={translations.labels.subject}
              name="subject"
              value={formData.subject}
              onChange={(option) => handleDropdownChange("subject", option)}
              options={data}
              placeholder={translations.labels.subjectPlaceholder}
              required
              error={submitted && errors.subject}
            />
          </div>

          {/* Chapter - text but digits only, max 4 */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.chapter}
              type="text"
              name="chapter"
              value={formData.chapter}
              onChange={handleNumericChange}
              inputMode="numeric"
              maxLength={4}
              placeholder={translations.labels.subjectPlaceholder}
              required
              error={submitted && errors.chapter}
            />
          </div>

          {/* Units */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.unit}
              name="units"
              value={formData.units}
              onChange={handleInputChange}
              placeholder={translations.labels.subjectPlaceholder}
              required
              error={submitted && errors.units}
            />
          </div>

          {/* Short Description */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.shortDescription}
              name="short_description"
              multiline
              rows={3}
              value={formData.short_description}
              onChange={handleInputChange}
              placeholder={translations.labels.shortDescriptionPlaceholder}
              required
              error={submitted && errors.short_description}
            />
          </div>

          {/* Description */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.description}
              name="description"
              multiline
              rows={3}
              className={styles.TextAreaStyles}
              value={formData.description}
              onChange={handleInputChange}
              placeholder={translations.labels.descriptionPlaceholder}
              required
              error={submitted && errors.description}
            />
          </div>

          {/* Page From - text but digits only, max 4 */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.pageFrom}
              type="text"
              name="page_from"
              value={formData.page_from}
              onChange={handleNumericChange}
              inputMode="numeric"
              maxLength={4}
              placeholder="E.g.1"
              required
              error={submitted && errors.page_from}
            />
          </div>

          {/* Page To - text but digits only, max 4 */}
          <div className="col-md-3 px-2">
            <CustomTextInput
              label={translations.labels.pageTo}
              type="text"
              name="page_to"
              value={formData.page_to}
              onChange={handleNumericChange}
              inputMode="numeric"
              maxLength={4}
              placeholder="E.g.5"
              required
              error={submitted && errors.page_to}
            />
          </div>

          {/* Exam Type */}
          <div className="col-md-3 px-2">
            <CustomDropdown
              label={translations.labels.examType}
              name="examType"
              value={formData.examType}
              onChange={(option) => handleDropdownChange("examType", option)}
              options={examData}
              placeholder={translations.labels.examTypePlaceholde}
              required
              error={submitted && errors.examType}
            />
          </div>

          {/* Action Buttons */}
          <div className={"col-md-3"}>
            <div className="row mt-2">
              <div className="col-md-6">
                <CustomButton
                  variant="contained"
                  onClick={handleAdd}
                  className={styles.saveButton}
                  fullWidth={false}
                  loading={loading}
                  loadingText="Saving..."
                >
                  <FaPlus style={{ marginRight: "8px" }} />
                  {translations.labels.add}
                </CustomButton>
              </div>
            </div>
          </div>

          {tableData.length > 0 && (
            <CustomMRTTable
              columns={columns}
              data={tableData}
              loading={loading}
              placeholder={translations.labels.searchButton}
              enableColumnFilters={false}
              enableGlobalFilter={false}
            />
          )}

          <div className="col-md-12">
            <div className="d-flex justify-content-center gap-3 mt-3">
              <CustomButton
                variant="contained"
                onClick={handleSubmit}
                className={styles.saveButton}
                fullWidth={false}
                loading={loading}
              >
                {translations.labels.saveButton}
              </CustomButton>

              <CustomButton
                variant="contained"
                onClick={handleCancel}
                className={styles.cancelButton}
                fullWidth={false}
                disabled={loading}
              >
                CANCEL
              </CustomButton>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default SyllabusCreate;
